import { TFunction } from 'i18next'
import { z } from 'zod'

import { Building } from '@/reducers/types'

// Note: the languages the backend stores for a building (Building.locale / availableLocale,
// EL-13870) and every app ships. Same list as BUSINESS_LANGUAGES in src/i18n (master side).
export const PLACE_LANGUAGES = ['en-US', 'fr-CA', 'es-US'] as const

export type PlaceLanguage = (typeof PLACE_LANGUAGES)[number]

// Note: English is the fallback of every app, so it is always offered and can't be removed
export const PLACE_FALLBACK_LANGUAGE: PlaceLanguage = 'en-US'

// Note: each language is shown in its own language, like the Profile language picker
export const PLACE_LANGUAGE_LABELS: Record<PlaceLanguage, string> = {
  'en-US': 'English (US)',
  'es-US': 'Español (EE. UU.)',
  'fr-CA': 'Français (Canada)',
}

export const makeLanguagesSchema = (t: TFunction<['organisation']>) =>
  z
    .object({
      availableLocale: z
        .array(z.enum(PLACE_LANGUAGES))
        .refine((languages) => languages.includes(PLACE_FALLBACK_LANGUAGE), {
          message: t('organisation:places.languages.errors.englishRequired'),
        }),
      i18nAvailable: z.boolean(),
      // Note: nullable so a cleared select fails with the "required" message, not a type error
      locale: z
        .enum(PLACE_LANGUAGES)
        .nullable()
        .refine((locale) => locale !== null, {
          message: t('organisation:places.languages.errors.defaultRequired'),
        }),
    })
    .superRefine(({ availableLocale, locale }, ctx) => {
      if (locale && !availableLocale.includes(locale)) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:places.languages.errors.defaultNotAvailable'),
          path: ['locale'],
        })
      }
    })

export type LanguagesFormData = z.input<ReturnType<typeof makeLanguagesSchema>>

export const isPlaceLanguage = (value: unknown): value is PlaceLanguage =>
  PLACE_LANGUAGES.includes(value as PlaceLanguage)

// Note: keeps the fixed order of PLACE_LANGUAGES, drops unknown tags and always adds English
export const normalizeLanguages = (languages: readonly unknown[]): PlaceLanguage[] =>
  PLACE_LANGUAGES.filter(
    (language) => language === PLACE_FALLBACK_LANGUAGE || languages.includes(language),
  )

// Note: `availableLocale` is a JSON column - one saved through the multipart building form comes
// back as the string '["en-US","fr-CA"]', so a string is parsed too
const readStoredLanguages = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string') return []
  try {
    const parsed: unknown = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch {
    return [value]
  }
}

export const getLanguagesDefaults = (building: Building): LanguagesFormData => {
  const locale = isPlaceLanguage(building.locale) ? building.locale : PLACE_FALLBACK_LANGUAGE

  return {
    // Note: the default language is always one of the offered ones
    availableLocale: normalizeLanguages([...readStoredLanguages(building.availableLocale), locale]),
    i18nAvailable: building.i18nAvailable === true,
    locale,
  }
}
